import { z } from "zod";
import { CONFLICT_MSG } from "./concurrent.js";
import { LineInput } from "./estimate.js";
import { RoundingCode, roundAmount } from "./rounding.js";

// ---------------------------------------------------------------------------
// 受け取った書類（受領書類。../eigyouryoku/docs/13、DB 20261011_1300。1回目＝保管と入れ方）
//   {customer}・取引先から届いた注文書・見積依頼・図面・写真・資料を、{project}の画面の枠「受け取った書類」に置いておく。
//   現場力・営業力の両方（門は core）。出し分けは使う機能 receivedDocs（画面で隠すだけ。API は止めない）。
//   原本は書き換えない（SHA-256）。削除は論理削除（「削除済み」から戻せる）。
//   2回目（R2、DB 20261011_2000）＝読み取り：注文書・見積依頼の行の「読み取る」→ 処理役（mai-11）が rd-1.0 で読む → 確認の引き出しで直す →
//   「見積作成」（新しい下書き）か「下書き見積に追加」。門はモジュール doc_read（有料オプション・既定 OFF）。
//   S3（R3、2026-10-11 夜の射場さんの決定）：画面で「AI」と名乗る＝「AI読み取り」「AI読み取り中」「AI読み取り結果」「AI書類読み取り」（「AI-OCR」「OCR」は書かない）
//   S2（現場力 #162）：種類の既定に「未選択」unsorted（PDF・TIFF・文字）。文字の層のある PDF は1ページ目の言葉で注文書・見積依頼・図面を既定に
// ---------------------------------------------------------------------------

/**
 * 種類（docs/13 決めごと3）。並びはプルダウンの順。unsorted＝未選択（S2、DB 20261011_1745）：人がまだ選んでいない。
 * プルダウンでは「—」で一番上（receivedKindOption）。読み取りに積めるのは order・inquiry だけ（DB のトリガー。unsorted は積めない）
 */
export const RECEIVED_KINDS = ["unsorted", "order", "inquiry", "invoice", "drawing", "photo", "material", "other"] as const;
export const ReceivedKind = z.enum(RECEIVED_KINDS);
export type ReceivedKind = z.infer<typeof ReceivedKind>;
export const RECEIVED_KIND_LABEL: Record<ReceivedKind, string> = {
  unsorted: "未選択",
  order: "注文書",
  inquiry: "見積依頼",
  invoice: "請求書",
  drawing: "図面",
  photo: "写真",
  material: "資料",
  other: "その他",
};

/** 形式（pdf／image＝JPEG・PNG・WebP・TIFF／text＝貼り付けた文字） */
export const ReceivedFormat = z.enum(["pdf", "image", "text"]);
export type ReceivedFormat = z.infer<typeof ReceivedFormat>;

/** 入れ方（API が受けるのは この4つ。email・fax は4回目の置き場所） */
export const ReceivedSource = z.enum(["upload", "drop", "paste", "mobile_camera"]);
export type ReceivedSource = z.infer<typeof ReceivedSource>;

/** 読み取りの状態（2回目。1回目は none のまま） */
export const ReceivedReadStatus = z.enum(["none", "queued", "running", "done", "failed"]);
export type ReceivedReadStatus = z.infer<typeof ReceivedReadStatus>;

/** プルダウンの文字（未選択は「—」。ほかは種類の名前） */
export const receivedKindOption = (k: ReceivedKind): string => (k === "unsorted" ? "—" : RECEIVED_KIND_LABEL[k]);

/**
 * 入れた瞬間の種類（docs/13 決めごと4、S2 で直した）：写真（JPEG・PNG・WebP）＝写真、PDF・TIFF・文字＝未選択。あとから人が変える。
 * PDF に文字の層があれば、API が1ページ目の言葉で注文書・見積依頼・図面にする（guessReceivedKind）
 */
export function defaultReceivedKind(format: ReceivedFormat, contentType?: string): ReceivedKind {
  return format === "image" && contentType !== "image/tiff" ? "photo" : "unsorted";
}

/** 1ページ目の文字の先頭から見る字数（S2） */
export const RECEIVED_GUESS_CHARS = 2000;
/** 言葉と種類（S2。AI は使わない＝文字の一致だけ）。注文書・見積依頼は先に出た方、どちらも無いときだけ図面 */
const GUESS_WORDS: { kind: "order" | "inquiry"; words: string[] }[] = [
  { kind: "order", words: ["注文書", "発注書"] },
  { kind: "inquiry", words: ["見積依頼", "御見積依頼", "見積書依頼"] },
];
/**
 * PDF の1ページ目の文字から種類の既定を当てる（S2）。当たらなければ null（＝未選択）。
 * 先頭 2,000 字だけ。全角・半角と康熙部首の字（Chromium の PDF の「⽂」など）は NFKC で揃え、字のあいだの空白（「注　文　書」）は詰めて見る
 */
export function guessReceivedKind(text: string): "order" | "inquiry" | "drawing" | null {
  const t = text.slice(0, RECEIVED_GUESS_CHARS).normalize("NFKC").replace(/\s+/g, "");
  let best: { kind: "order" | "inquiry"; at: number } | null = null;
  for (const g of GUESS_WORDS) {
    for (const w of g.words) {
      const at = t.indexOf(w);
      if (at >= 0 && (!best || at < best.at)) best = { kind: g.kind, at };
    }
  }
  if (best) return best.kind;
  return t.includes("図面") ? "drawing" : null;
}

/**
 * 受け付ける形式・大きさ（docs/13 決めごと15・§5。DB の CHECK と同じ数）。
 * TIFF は FAX の受信（複合機・インターネット FAX が TIFF で渡す）。形式は image、ページは PDF と同じく20まで（S1）
 */
export const RECEIVED_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/tiff"] as const;
/** ファイルの名前の拡張子（ブラウザが形式を付けないときの見分け） */
export const RECEIVED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "webp", "tif", "tiff"] as const;
/** 「ファイル選択」の accept（PC。FileDrop・貼り付け・枠の右上の「ファイル選択」が同じこれを使う） */
export const RECEIVED_ACCEPT = [...RECEIVED_CONTENT_TYPES, ".tif", ".tiff"].join(",");
/** スマホ版の accept（写真を撮る／ライブラリ／ファイル が出る形。image/* は TIFF も含む） */
export const RECEIVED_PHONE_ACCEPT = "image/*,application/pdf";
/** 受け付けるファイルか（形式か名前の拡張子で。中身は API が見る） */
export function isReceivedFile(f: { type: string; name: string }): boolean {
  if ((RECEIVED_CONTENT_TYPES as readonly string[]).includes(f.type)) return true;
  const ext = /\.([a-z0-9]+)$/i.exec(f.name)?.[1]?.toLowerCase();
  return !!ext && (RECEIVED_EXTENSIONS as readonly string[]).includes(ext);
}
/** 送るときの Content-Type（ブラウザが付けなければ名前から。API は中身で決めるので目安） */
export function receivedTypeOf(f: { type: string; name: string }): string {
  if (f.type) return f.type;
  const ext = /\.([a-z0-9]+)$/i.exec(f.name)?.[1]?.toLowerCase() ?? "";
  return ext === "pdf" ? "application/pdf" : ext === "png" ? "image/png" : ext === "webp" ? "image/webp" : ext === "tif" || ext === "tiff" ? "image/tiff" : ext === "jpg" || ext === "jpeg" ? "image/jpeg" : "application/octet-stream";
}
export const RECEIVED_MAX_BYTES = 20 * 1024 * 1024;
export const RECEIVED_MAX_PAGES = 20;
/** 一度に落とす・選ぶのは10件まで（11件目からは送らない、S2） */
export const RECEIVED_BATCH_MAX = 10;
/** 1件を送って返事を待つのは2分まで（打ち切って「送れませんでした」、次へ。S2） */
export const RECEIVED_UPLOAD_TIMEOUT_MS = 120_000;
export const RECEIVED_GROUP_LIMIT = 100;
export const RECEIVED_TITLE_MAX = 200;
export const RECEIVED_TEXT_MIN = 20;
export const RECEIVED_TEXT_MAX = 20_000;
/** 画像の表示用の長辺（px）。型番シールが読める境界（CLAUDE.md「写真の大きさ」と同じ） */
export const RECEIVED_DISPLAY_PX = 1600;

/** 一覧の1行 */
export const ReceivedDocument = z.object({
  id: z.string().uuid(),
  kind: ReceivedKind,
  format: ReceivedFormat,
  title: z.string(),
  /** 原本の大きさ（バイト）。文字の書類は本文の大きさ */
  bytes: z.number().int(),
  /** PDF・TIFF のページ数（ほかの画像・文字は null） */
  pageCount: z.number().int().nullable(),
  /** 入れた人の氏名（外した人・消えた人は null） */
  uploadedBy: z.string().nullable(),
  createdAt: z.string(),
  /** 削除した日時（削除済みの一覧だけ） */
  deletedAt: z.string().nullable(),
  readStatus: ReceivedReadStatus,
  /** 読み取れなかったときの文（read_status=failed。2回目） */
  readError: z.string().nullable(),
  /** 読み取ったときの種類の提案（order／inquiry／other。読んでいなければ null） */
  readKindGuess: z.enum(["order", "inquiry", "other"]).nullable(),
  /**
   * 読み取りの警告（S4、docs/13 §4.6 ①）。sender＝読み取った差出人が{project}の{customer}と異なる（read_status=done で取り込む前だけ）。
   * 判定は GET …/read の sender.mismatch と同じ関数（API の kits/received/sender.ts）
   */
  readWarnings: z.array(z.enum(["sender"])),
  /** 送った見積依頼の添付に使った（#218、docs/88 §4.6「依頼に使用 RQ-0003 沼田電気」）。下書きの依頼は数えない。番号の順 */
  usedIn: z.array(z.object({ id: z.string().uuid(), no: z.string(), vendor: z.string() })),
  /**
   * 取り込んだ見積（2回目）・仕入（#233 3a、請求書）。mode＝new（見積を作成）／append（下書きの見積に追加）／purchase（仕入を登録）。
   * target＝estimate（documentId は見積）／purchase（documentId は仕入）。見積・仕入（下書き）を消すと null に戻る
   */
  imported: z
    .object({ documentId: z.string().uuid(), documentNo: z.string().nullable(), mode: z.enum(["new", "append", "purchase"]).nullable(), target: z.enum(["estimate", "purchase"]).default("estimate") })
    .nullable(),
  /** 原本（PDF・画像）の署名つき URL。文字の書類は null。15〜30分で切れる（一覧を読み直すと新しくなる） */
  originalUrl: z.string().nullable(),
  /** 画像（JPEG・PNG・WebP）の表示用（長辺 1600px の JPEG）の署名つき URL。無ければ null。PDF・TIFF はページ画像（/pages）だけ（S2 から TIFF も null） */
  displayUrl: z.string().nullable(),
  /** 原本のダウンロード（Content-Disposition: attachment）の署名つき URL。文字の書類は null */
  downloadUrl: z.string().nullable(),
  /** 原本の形式（application/pdf・image/jpeg…）。文字の書類は text/plain */
  contentType: z.string(),
});
export type ReceivedDocument = z.infer<typeof ReceivedDocument>;

/** ページ画像（表示用、長辺 1600px の JPEG。PDF・TIFF はページごと、ほかの画像は1枚）。url は署名つき（15〜30分） */
export const ReceivedPage = z.object({ n: z.number().int(), url: z.string(), width: z.number().int().nullable(), height: z.number().int().nullable() });
export type ReceivedPage = z.infer<typeof ReceivedPage>;
/**
 * GET /received-documents/:id/pages（S1）。PDF・TIFF は初めて開いたときに作って保存する（S2：アップロードでは作らない＝送るのを待たせない）。
 * 同時に開いても描くのは1組（API が進行中の描画を分ける）。
 * 作れなかった（読めない PDF・描く部品が無い）ときは空（画面は「原本表示」と「ダウンロード」だけ）
 */
export const ReceivedPages = z.object({ id: z.string().uuid(), pages: z.array(ReceivedPage) });
export type ReceivedPages = z.infer<typeof ReceivedPages>;

/** GET /groups/:id/received-documents（?deleted=1 で削除済み） */
export const ReceivedList = z.object({
  items: z.array(ReceivedDocument),
  /** 削除済みの件数（「削除済み n ›」） */
  deletedCount: z.number().int(),
  /** この{project}の書類の件数（削除済みを除く）と上限 */
  count: z.number().int(),
  limit: z.number().int(),
  /** 今月の読み取りの通数と上限（{vendorQuote}と合算。上限なし＝受付開始前は null） */
  quota: z.object({ used: z.number().int(), limit: z.number().int().nullable() }),
});
export type ReceivedList = z.infer<typeof ReceivedList>;

/** PATCH /received-documents/:id */
export const ReceivedPatch = z
  .object({
    kind: ReceivedKind.optional(),
    title: z.string().trim().min(1, "名前を入れてください").max(RECEIVED_TITLE_MAX, `名前は${RECEIVED_TITLE_MAX}文字までです`).optional(),
  })
  .refine((v) => v.kind !== undefined || v.title !== undefined, { message: "変えるものがありません" });
export type ReceivedPatch = z.infer<typeof ReceivedPatch>;

/** POST /groups/:id/received-documents/text（貼り付けた文字） */
export const ReceivedTextInput = z.object({
  title: z.string().trim().max(RECEIVED_TITLE_MAX).optional(),
  body: z
    .string()
    .refine((s) => s.trim().length >= RECEIVED_TEXT_MIN, { message: `${RECEIVED_TEXT_MIN}文字以上の文字を貼り付けてください` })
    .refine((s) => s.length <= RECEIVED_TEXT_MAX, { message: `文字は${RECEIVED_TEXT_MAX.toLocaleString("ja-JP")}字までです` }),
});
export type ReceivedTextInput = z.infer<typeof ReceivedTextInput>;

/** GET /received-documents/:id/text */
export const ReceivedText = z.object({ id: z.string().uuid(), title: z.string(), body: z.string() });
export type ReceivedText = z.infer<typeof ReceivedText>;

/** 元のファイル名をヘッダ x-gb-title に入れる形（RFC 8187：UTF-8''…）。日本語のファイル名もそのまま運ぶ */
export function encodeTitleHeader(name: string): string {
  return `UTF-8''${encodeURIComponent(name)}`;
}
/** x-gb-title を読む。形が違えば null（API はそのとき既定の名前） */
export function decodeTitleHeader(v: string | undefined | null): string | null {
  if (!v) return null;
  const m = /^UTF-8''(.*)$/i.exec(v.trim());
  if (!m) return null;
  try {
    const s = decodeURIComponent(m[1]!).replace(/[\u0000-\u001f\u007f]/g, "").trim();
    return s ? s.slice(0, RECEIVED_TITLE_MAX) : null;
  } catch {
    return null;
  }
}

const JST = 9 * 60 * 60 * 1000;
/** 「10/09 19:40」（日本時間） */
export function receivedStamp(d: Date): string {
  const j = new Date(d.getTime() + JST);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(j.getUTCMonth() + 1)}/${p(j.getUTCDate())} ${p(j.getUTCHours())}:${p(j.getUTCMinutes())}`;
}
/** 貼り付けた文字・画像の名前（docs/13 §4.2） */
export const pastedTextTitle = (d: Date): string => `貼り付けた文字 ${receivedStamp(d)}`;
export const pastedImageTitle = (d: Date): string => `貼り付けた画像 ${receivedStamp(d)}`;

/** 貼り付けのキー（Mac は ⌘V、ほかは Ctrl+V）。platform は navigator.platform（無ければ userAgent） */
export function pasteKeyOf(platform: string): string {
  return /Mac|iPhone|iPad|iPod/i.test(platform) ? "⌘V" : "Ctrl+V";
}

/** 画面・API の文（{customer}・{project} は会社の言葉＝画面は t()、API は fillLabels） */
export const RECEIVED_MSG = {
  /** 送った見積依頼に付けた書類のしるし（#218、docs/88 §4.6） */
  usedIn: "依頼に使用 {no} {vendor}",
  usedInMany: "依頼に使用 {n}件",
  title: "受け取った書類",
  help: "{customer}から受け取った注文書・見積依頼・図面・写真などを{project}に置いておく枠です。ファイルはこの枠か画面のどこかにドロップするか、「ファイル選択」で入れます。文字は {paste} で貼り付けると書類になります。原本はそのまま保存し、削除しても「削除済」から復元できます。",
  /** #280：「落とす」→「ドロップ」・「文字」→「テキスト」（docs/99 §10） */
  dropHint: "ここにドロップ（PDF・写真）　テキストは {paste} で貼り付け",
  dropHintVendor: "ここにドロップ（PDF・写真）",
  overlay: "受け取った書類に追加",
  choose: "ファイル選択",
  col: { kind: "書類", name: "名前", date: "登録日時", by: "登録者", state: "状態" },
  pages: (n: number) => `P.${n}`,
  /** 枠の題の右（0件なら出さない、S2。S4 で「種類」→「書類」） */
  unsortedCount: (n: number) => `書類が未選択 ${n}件`,
  /** スマホの枠の題の右（幅が無い、S4） */
  unsortedCountShort: (n: number) => `未選択 ${n}件`,
  /** 未選択の行の書類のチップ・プルダウンの文（S4。「〜して下さい」の文にしない＝docs/10 §5.1.2） */
  pickKind: "書類選択",
  /** 書類を選ぶシートの題（S4、スマホ） */
  kindSheetTitle: "書類",
  /** 送る順番を待っている行（1つずつ送る、S2） */
  waiting: "順番待ち",
  adding: "追加中…",
  retry: "もう一度",
  cancel: "取消",
  /** #280：「削除済み」→「削除済」・「戻す」→「復元」 */
  deleted: "削除済",
  restore: "復元",
  download: "ダウンロード",
  /** スマホの PDF：原本を新しいタブで（Safari の PDF 表示） */
  openOriginal: "原本表示",
  /** ページ画像の代わりの文（作れなかった） */
  noPages: "ページの画像を表示できません。「原本表示」か「ダウンロード」で見てください",
  pageAlt: (title: string, n: number) => `${title}（${n}ページ目）`,
  /** ページ画像ができるまで（初めて開いたときに API が作る、S2） */
  preparing: "準備中…",
  rename: "名前変更",
  renameTitle: "名前変更",
  renameSave: "名前保存",
  delete: "削除",
  close: "閉じる",
  more: "その他の操作",
  deleteBody: "「{title}」を削除します。「削除済」から復元できます。",
  deletedToast: "削除しました",
  restoredToast: "復元しました",
  /** 削除済の行の「完全に削除」（#281、docs/99 §11。元に戻せない＝窓で確かめる） */
  purge: "完全に削除",
  purgeTitle: "完全に削除",
  purgeBody: "「{title}」を完全に削除します。原本も消え、元に戻せません。",
  purgedToast: "完全に削除しました",
  /** API の断り（#281） */
  purgeNotDeleted: "削除済の書類だけ完全に削除できます",
  purgeRfq: "見積依頼 {no} に添付しているため削除できません（先に見積依頼を取消）",
  purgePurchase: "確定した仕入の元の書類のため削除できません",
  kindToast: "書類を「{kind}」にしました",
  pasteSheetTitle: "貼り付けた文字を書類として追加",
  pasteAdd: "書類として追加",
  pasteHint: "{n}文字",
  text: "貼り付けた文字",
  noPreview: "このファイルは表示できません。「ダウンロード」で開いてください",
  // エラー（色だけにしない＝朱の文字で行の下に）
  type: "PDF・JPEG・PNG・WebP・TIFF のファイルを選んでください",
  broken: "ファイルを読めませんでした（壊れているか、パスワードがかかっています）",
  tooBig: "1つのファイルは20MBまでです",
  /** 一度に落とす・選ぶのは10件まで（11件目から、送らずに行で知らせる。S2） */
  tooMany: `一度に入れられるのは${RECEIVED_BATCH_MAX}件までです`,
  /** 1件を2分待っても返事が無い（打ち切って次へ。S2） */
  timeout: "送れませんでした",
  tooManyPages: "PDF・TIFF は20ページまでです",
  limit: "受け取った書類は1つの{project}に100件までです",
  storageFull: "保存できる容量（{gb}GB）を超えるため追加できません。不要な書類を整理するか、料金プランをご覧ください",
  textTooShort: `${RECEIVED_TEXT_MIN}文字以上の文字を貼り付けてください`,
  textTooLong: `文字は${RECEIVED_TEXT_MAX.toLocaleString("ja-JP")}字までです`,
  notFound: "書類が見つかりません",
  groupNotFound: "{project}が見つかりません",
  /** 案件を削除しようとしたとき（受け取った書類が残っていれば消さない） */
  groupHasDocs: "受け取った書類があるため、この{project}は削除できません",
} as const;

// ---------------------------------------------------------------------------
// 読み取り（2回目。../eigyouryoku/docs/13 §4.3・§6・§7、依頼文 R2 §4）
//   処理役（mai-11）は書かれたままの文字（rd-1.0）を返す。数字・日付を直すのは API（apps/api/src/kits/received/normalize.ts）。
//   read_result は rd-1.0 の生のまま保存し、GET …/read が返すたびに直す（正規化した値は保存しない）
// ---------------------------------------------------------------------------

/**
 * 処理役の読み取りの版（プロンプトと JSON の形）。rd-1.1（S5、../eigyouryoku/docs/13 §4.7）＝行を紙ごと（sheets）・全部の行を上から順に・行に種類の候補 k。
 * rd-1.0 の保存済みの結果（lines だけ）は「表紙1枚・全部 item」として読む（receivedRawSheets）
 */
export const RD_VERSION = "rd-1.1";

/** 読み取りの行の種類の候補（LLM が付ける。{vendorQuote}の vq-1.2 と同じ7つ）。最終の区分は classifyReceivedLines が決める */
export const RECEIVED_RAW_KS = ["item", "head", "subtotal", "total", "discount", "tax", "note"] as const;
export type ReceivedRawK = (typeof RECEIVED_RAW_KS)[number];

const RawText = (max: number) => z.string().max(max).default("");
/** 読み取った品目の1行（rd-1.0／rd-1.1）。数字も文字のまま（「２２０個」「¥1,200」）。k は rd-1.1 だけ（無い・知らない値は item） */
export const ReceivedReadRawLine = z.object({
  k: z.enum(RECEIVED_RAW_KS).catch("item"),
  name: RawText(400),
  spec: RawText(400),
  qty: RawText(100),
  unit: RawText(40),
  unitPrice: RawText(100),
  amount: RawText(100),
  remarks: RawText(400),
});
export type ReceivedReadRawLine = z.infer<typeof ReceivedReadRawLine>;

/** 読み取った1枚の紙（rd-1.1）。表紙（宛先・合計のある紙）は cover、内訳書（品目の一覧）は breakdown */
export const ReceivedReadRawSheet = z.object({
  name: RawText(200),
  kind: z.enum(["cover", "breakdown"]).catch("cover"),
  lines: z.array(ReceivedReadRawLine).max(500).default([]),
});
export type ReceivedReadRawSheet = z.infer<typeof ReceivedReadRawSheet>;

/** 処理役の POST …/result（kind: "received"）の result＝rd-1.1（rd-1.0 も読める）の生の JSON */
export const ReceivedReadRaw = z.object({
  v: z.string().max(20).default(RD_VERSION),
  kindGuess: z.enum(["order", "inquiry", "other"]).default("other"),
  sender: z
    .object({ company: RawText(200), person: RawText(100), tel: RawText(60), fax: RawText(60) })
    .default({ company: "", person: "", tel: "", fax: "" }),
  docNo: RawText(100),
  datedText: RawText(100),
  subject: RawText(400),
  deliverTo: RawText(400),
  dueText: RawText(200),
  taxIncluded: z.boolean().nullable().default(null),
  totalText: RawText(100),
  /** rd-1.0 の行（rd-1.1 は sheets）。sheets があれば使わない */
  lines: z.array(ReceivedReadRawLine).max(500).default([]),
  /** rd-1.1 の紙（S5）。無い（rd-1.0）・空なら lines を表紙1枚に */
  sheets: z.array(ReceivedReadRawSheet).max(50).optional(),
  bodyText: RawText(20_000),
  confidence: z.object({ head: z.number().min(0).max(1), lines: z.number().min(0).max(1) }).default({ head: 0, lines: 0 }),
});
export type ReceivedReadRaw = z.infer<typeof ReceivedReadRaw>;

/** 読み取りの紙（rd-1.1 はそのまま。rd-1.0 の保存済みの結果は lines を「表紙1枚・全部 item」に） */
export function receivedRawSheets(raw: Pick<ReceivedReadRaw, "lines" | "sheets">): ReceivedReadRawSheet[] {
  if (raw.sheets && raw.sheets.length) return raw.sheets;
  return [{ name: "", kind: "cover", lines: raw.lines.map((l) => ({ ...l, k: "item" as const })) }];
}

/** 信頼度がこれより下の欄・行は「確かめて」（#81 と同じ 0.89） */
export const RECEIVED_READ_SURE = 0.89;

// ---------------------------------------------------------------------------
// 検算（S4、docs/13 §4.6 ③）：TS で（LLM にさせない）。API の normalize.ts が出し、人が数量・単価を直すと画面が同じ関数で計算し直す
// ---------------------------------------------------------------------------

/** ok＝合う／mismatch＝合わない／none＝比べられない（片方が無い） */
export const ReceivedCheck = z.enum(["ok", "mismatch", "none"]);
export type ReceivedCheck = z.infer<typeof ReceivedCheck>;

/**
 * 数量×単価（会社の明細の丸め＝見積の行と同じ）。どちらか無ければ null。
 * 数量は小数2桁なので 100倍の整数で掛けてから丸める（1.15×100 の浮動小数の誤差で切り捨てが1円ずれない。正は見積にしたあとの DB の値）
 */
export function receivedLineAmount(quantity: number | null | undefined, unitPrice: number | null | undefined, code: RoundingCode | string): number | null {
  if (quantity == null || unitPrice == null || !Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return null;
  const prod = Math.round(quantity * 100) * unitPrice;
  return Number.isSafeInteger(prod) ? roundAmount(prod / 100, code) : roundAmount(quantity * unitPrice, code);
}

/** 行の検算：数量×単価 と 読み取った金額。両方あるときだけ比べる */
export const receivedLineCheck = (computed: number | null, amountRead: number | null): ReceivedCheck =>
  computed == null || amountRead == null ? "none" : computed === amountRead ? "ok" : "mismatch";

/**
 * 行の合計（S5）：合計に入れる行（明細・率・値引き。区分の無い行は明細）だけを足す。
 * 明細は 数量×単価（無ければ読み取った金額）、率は読み取った金額、値引きは読み取った金額をマイナスに。どの行にも無ければ null
 */
export function receivedLineSum(lines: { kind?: ReceivedLineKind; computed: number | null; amountRead: number | null }[]): number | null {
  let any = false;
  let sum = 0;
  for (const l of lines) {
    const v = receivedLineValue(l);
    if (v == null) continue;
    any = true;
    sum += v;
  }
  return any ? sum : null;
}

/** 消費税の候補（相手の端数処理は分からないので 切り捨て・四捨五入・切り上げ の3つ）。税率は 0.1 の形・100分の1% の整数にしてから計算 */
function taxCandidates(sum: number, taxRate: number): number[] {
  const bp = Math.round(taxRate * 10_000);
  const n = sum * bp;
  if (!Number.isSafeInteger(n)) return [];
  const q = Math.trunc(n / 10_000);
  const r = n - q * 10_000;
  return [...new Set([q, r * 2 >= 10_000 ? q + 1 : q, r > 0 ? q + 1 : q])];
}

/**
 * 合計の検算：読み取った合計 と 行の合計。
 *   合えば ok（taxIncluded=false）。合わず、取引日の税率で 行の合計＋消費税 と合えば ok（taxIncluded=true＝税込の合計と比べた）。
 *   税率が引けないとき（取引日が 2019-10-01 より前など）は 10% を決め打ちせず、書類が税抜と言っていなければ none
 */
export function receivedTotalCheck(read: number | null, sum: number | null, taxRate: number | null, taxIncludedHint: boolean | null): { check: ReceivedCheck; taxIncluded: boolean } {
  if (read == null || sum == null) return { check: "none", taxIncluded: false };
  if (read === sum) return { check: "ok", taxIncluded: false };
  if (taxRate == null || taxRate <= 0) return { check: taxIncludedHint === false ? "mismatch" : "none", taxIncluded: false };
  if (taxCandidates(sum, taxRate).some((tax) => sum + tax === read)) return { check: "ok", taxIncluded: true };
  return { check: "mismatch", taxIncluded: false };
}

// ---------------------------------------------------------------------------
// 行の区分と階層の検算（S5、../eigyouryoku/docs/13 §4.7・決めごと19、依頼文 R5 §2）
//   LLM（rd-1.1）は全部の行を上から順に写し、種類の候補 k と紙（表紙／内訳）を付ける。最終の区分と検算はここ（TS）の決まり＝LLM に計算させない。
//   {vendorQuote}の読み取り（vq-1.2）の roleOf・check（apps/gpu-worker/src/normalize.ts）と同じ考え。
//   classifyReceivedLines は API（kits/received/normalize.ts）だけが呼ぶ。receivedCheckAll は API と画面（人が区分・数量・単価を直したとき）が呼ぶ
// ---------------------------------------------------------------------------

/**
 * 行の区分。合計に入れるのは 明細・率・値引き。見出し・説明・別途は取り込むが合計に入れない。
 * 小計・内訳の合計（表紙にある内訳の紙の合計の行）・合計・消費税は検算にだけ使い、取り込まない
 */
export const RECEIVED_LINE_KINDS = ["detail", "heading", "note", "rate", "excluded", "discount", "subtotal", "sheetTotal", "total", "tax"] as const;
export const ReceivedLineKind = z.enum(RECEIVED_LINE_KINDS);
export type ReceivedLineKind = z.infer<typeof ReceivedLineKind>;
/** 区分の言葉（列の名前は「区分」＝見積の表と同じ。受け取った書類の画面に「種類」の字を書かない） */
export const RECEIVED_LINE_KIND_LABEL: Record<ReceivedLineKind, string> = {
  detail: "明細",
  heading: "見出し",
  note: "説明",
  rate: "率",
  excluded: "別途",
  discount: "値引き",
  subtotal: "小計",
  sheetTotal: "内訳の合計",
  total: "合計",
  tax: "消費税",
};
/** 人がセレクトで選べる区分（見積の行の区分に写るもの）。小計・合計・消費税・内訳の合計は選べない（検算のみの行を「明細」などに変えることはできる） */
export const RECEIVED_LINE_KIND_SELECTABLE = ["detail", "heading", "note", "rate", "excluded", "discount"] as const satisfies readonly ReceivedLineKind[];
/** 合計に入れる行か（区分の無い行＝前の形は明細） */
export const receivedInTotal = (k: ReceivedLineKind | undefined): boolean => k === undefined || k === "detail" || k === "rate" || k === "discount";
/** 検算にだけ使う行か（取り込まない） */
export const receivedCheckOnly = (k: ReceivedLineKind | undefined): boolean => k === "subtotal" || k === "sheetTotal" || k === "total" || k === "tax";

/** 金額の欄の「別途」（合計に入れない）。{vendorQuote}の EXCLUDED_WORDS と同じ（apps/gpu-worker/src/normalize.ts。食い違いは API のテストが落とす） */
export const RECEIVED_EXCLUDED_WORDS = ["別途"] as const;
/** 金額の欄の「込」など（{vendorQuote}の ZERO_WORDS と同じ）。受け取った書類では 別途 と同じく合計に入れず、金額の欄の文字のまま取り込む */
export const RECEIVED_ZERO_WORDS = ["込", "含む", "在庫", "サービス", "無料", "支給"] as const;

/**
 * 名前を比べる形：NFKC・空白を詰める・全体のかっこを外す（「（小　計）」→ 小計）・末尾のかっこ書きと率を外す（「合計（税抜）」「消費税(10%)」「消費税10%」→ 合計・消費税）
 */
export function receivedLabel(s: string): string {
  let t = (s ?? "").normalize("NFKC").replace(/\s+/g, "");
  for (let i = 0; i < 4; i++) {
    const whole = /^[(\[【〔<]([^()[\]【】〔〕<>]*)[)\]】〕>]$/.exec(t);
    if (whole) {
      t = whole[1]!;
      continue;
    }
    const next = t.replace(/[(\[【〔<][^()[\]【】〔〕<>]*[)\]】〕>]$/, "").replace(/[0-9]+(?:\.[0-9]+)?%$/, "");
    if (next === t || !next) break;
    t = next;
  }
  return t;
}
const TAX_NAME = /^内?(消費税|消費税等|消費税額|消費税等額|税額|税)$/;
const TOTAL_NAME = /(合計|総計|総額)(金額|額)?$|^(御|お)?見積(金額|総額)$|^(税込|税抜|税別)(合計|金額|価格|額)?$/;
/** 小計・計・「○○計」（材料費計・労務費計・1階分計）。「設計」「会計」は品目なので入れない */
const SUBTOTAL_NAME = /小計$|^計$|(費|料|分|代|工事|部材)計$/;
const DISCOUNT_NAME = /値引|割引|値下/;
const RATE_RE = /([0-9]+(?:\.[0-9]+)?)\s*(?:%|パーセント)|([0-9]+(?:\.[0-9]+)?)\s*割/;
const ONLY_RATE = /^[0-9]+(?:\.[0-9]+)?(?:%|パーセント|割)$/;

/** 率の文字（「8%」「８％」「8パーセント」→ 8%、「1割」→ 1割）。最初に見つかった欄の1つ。無ければ null */
export function receivedRateText(...texts: (string | null | undefined)[]): string | null {
  for (const s of texts) {
    const m = RATE_RE.exec((s ?? "").normalize("NFKC"));
    if (m) return m[1] != null ? `${m[1]}%` : `${m[2]}割`;
  }
  return null;
}
/** 率の文字を 0.1 の形に（「10%」→ 0.1、「1割」→ 0.1）。読めなければ null */
function rateValue(text: string | null): number | null {
  if (!text) return null;
  const m = /^([0-9.]+)(%|割)$/.exec(text);
  if (!m) return null;
  const v = Number(m[1]);
  return Number.isFinite(v) && v > 0 ? (m[2] === "%" ? v / 100 : v / 10) : null;
}
const filledText = (s: string | null | undefined) => (s ?? "").trim() !== "";
const onlyRate = (s: string) => ONLY_RATE.test(s.normalize("NFKC").replace(/\s+/g, ""));
/** 金額の欄の文字が「別途」「込」など（数字を含まない）か */
function amountWord(text: string): string | null {
  const t = (text ?? "").normalize("NFKC").trim();
  if (!t || /[0-9]/.test(t)) return null;
  return [...RECEIVED_EXCLUDED_WORDS, ...RECEIVED_ZERO_WORDS].some((w) => t.includes(w)) ? t.slice(0, 20) : null;
}
/**
 * 表の列の見出しの行（「項目｜数量｜単位｜単価｜金額」「品名｜規格｜数量…」）か（S5。本物の vLLM で N-2026-02 を読むと k=head で入った）。
 * 名称の欄が列の名前で、ほかの空でない欄もすべて列の名前（数字を含まない）なら見出しの行＝読み取りの行から外す（API の normalize が空の行と一緒に捨てる）
 */
const HEADER_NAME = /^(項目|品名|品目|名称|摘要|内容|工事名|工事名称|工事項目|工種|品名規格|名称規格|品名・規格|名称・規格|工事内容)$/;
const HEADER_OTHER = /^(規格|仕様|型番|寸法|規格・仕様|仕様・規格|形状寸法|数量|数|単位|単価|金額|価格|備考|摘要)$/;
export function isReceivedHeaderRow(l: Pick<ReceivedReadRawLine, "name" | "spec" | "qty" | "unit" | "unitPrice" | "amount" | "remarks">): boolean {
  if (!HEADER_NAME.test(receivedLabel(l.name))) return false;
  return [l.spec, l.qty, l.unit, l.unitPrice, l.amount, l.remarks].every((x) => {
    const t = receivedLabel(x ?? "");
    return t === "" || HEADER_OTHER.test(t);
  });
}

/** 内訳の名前を比べる鍵（末尾の「計」「小計」「合計」を外す） */
const sheetKey = (s: string) => receivedLabel(s).replace(/(小計|合計|計)$/, "");

/** 区分を決める材料：読み取った1行（文字のまま）＋金額の欄を円にした値（API が読む。読めなければ null・▲や − はマイナス） */
export type ReceivedClassifyLine = Pick<ReceivedReadRawLine, "k" | "name" | "spec" | "qty" | "unit" | "unitPrice" | "amount" | "remarks"> & { yen: number | null };
export type ReceivedClassifySheet = { name: string; kind: "cover" | "breakdown"; lines: ReceivedClassifyLine[] };
/** 決まった区分。amount＝合計に使う金額（値引きはマイナス、見出し・説明・別途は null）、amountLabel＝別途の行の金額の欄の文字、rateText＝率の行の率の文字 */
export type ReceivedClassified = { kind: ReceivedLineKind; amount: number | null; amountLabel: string | null; rateText: string | null };

function classifyOne(l: ReceivedClassifyLine, onCover: boolean, sheetTotals: Set<number>, labels: Set<string>): ReceivedClassified {
  const t = receivedLabel(l.name);
  const qtyEmpty = !filledText(l.qty);
  const priceEmpty = !filledText(l.unitPrice);
  const noQP = qtyEmpty && priceEmpty;
  const yen = l.yen;
  const out = (kind: ReceivedLineKind, extra: Partial<ReceivedClassified> = {}): ReceivedClassified => ({ kind, amount: yen, amountLabel: null, rateText: null, ...extra });
  // (a) 合計・消費税（数量・単価が空）
  if (noQP && (l.k === "tax" || TAX_NAME.test(t))) return out("tax");
  if (noQP && (l.k === "total" || TOTAL_NAME.test(t))) return out("total");
  // (b) 小計・計・○○計
  if (noQP && (l.k === "subtotal" || SUBTOTAL_NAME.test(t))) return out("subtotal");
  // (c) 金額の欄が「別途」「込」など
  const word = yen == null ? amountWord(l.amount) : null;
  if (word) return out("excluded", { amount: null, amountLabel: word });
  if (l.k === "note" && yen == null) return out("note", { amount: null });
  if (yen == null && [l.name, l.spec, l.remarks].some((s) => RECEIVED_EXCLUDED_WORDS.some((w) => (s ?? "").includes(w)))) return out("excluded", { amount: null, amountLabel: RECEIVED_EXCLUDED_WORDS[0] });
  // (d) 値引き（金額がマイナス・k=discount・名前に 値引／割引）。金額はマイナスの整数
  if (yen != null && (yen < 0 || l.k === "discount" || DISCOUNT_NAME.test(t))) return out("discount", { amount: -Math.abs(yen) });
  // (e) 率（数量・単価が空か率の文字だけ、金額があり、名前・規格・備考・数量・単価に n% が読める）
  if (yen != null && (qtyEmpty || onlyRate(`${l.qty}${l.unit}`)) && (priceEmpty || onlyRate(l.unitPrice))) {
    const rt = receivedRateText(l.name, l.spec, l.remarks, `${l.qty}${l.unit}`, l.unitPrice);
    if (rt) return out("rate", { rateText: rt });
  }
  // (f) 数量・単価・金額が全部空 → 見出し（k=note なら説明）
  if (yen == null && noQP) return out(l.k === "note" ? "note" : "heading", { amount: null });
  // (g) 表紙の、内訳の紙（や内訳の小計）と同じ名前か同じ金額の行 → 内訳の合計（二重に数えない）
  if (onCover && yen != null && (sheetTotals.has(Math.abs(yen)) || (sheetKey(l.name) !== "" && labels.has(sheetKey(l.name))))) return out("sheetTotal");
  // (h)
  return out("detail");
}

/**
 * 区分を決める（S5。純粋な関数・API だけが呼ぶ）。k は候補で、決まり (a)〜(h) が優先（LLM が見出しと言っても金額があれば明細）。
 * 先に内訳の紙を決め、その小計・合計の金額と名前で表紙の「内訳の合計」の行を見分ける。並びは sheets・lines と同じ
 */
export function classifyReceivedLines(sheets: ReceivedClassifySheet[]): ReceivedClassified[][] {
  const out: ReceivedClassified[][] = sheets.map(() => []);
  const sheetTotals = new Set<number>();
  const labels = new Set<string>();
  sheets.forEach((s, i) => {
    if (s.kind !== "breakdown") return;
    const cls = s.lines.map((l) => classifyOne(l, false, sheetTotals, labels));
    out[i] = cls;
    if (sheetKey(s.name)) labels.add(sheetKey(s.name));
    let sum = 0;
    cls.forEach((c, j) => {
      const l = s.lines[j]!;
      if ((c.kind === "subtotal" || c.kind === "total") && c.amount != null) {
        sheetTotals.add(Math.abs(c.amount));
        for (const x of [l.name, l.spec]) if (sheetKey(x)) labels.add(sheetKey(x));
      }
      if (receivedInTotal(c.kind) && c.amount != null) sum += c.amount;
    });
    if (sum > 0) sheetTotals.add(sum);
  });
  sheets.forEach((s, i) => {
    if (s.kind === "breakdown") return;
    out[i] = s.lines.map((l) => classifyOne(l, true, sheetTotals, labels));
  });
  return out;
}

/** 合計に入れる行の金額（明細＝数量×単価、無ければ読み取った金額／率＝読み取った金額／値引き＝読み取った金額をマイナスに）。入れない行は null */
export function receivedLineValue(l: { kind?: ReceivedLineKind; computed: number | null; amountRead: number | null }): number | null {
  if (!receivedInTotal(l.kind)) return null;
  if (l.kind === "rate") return l.amountRead;
  if (l.kind === "discount") return l.amountRead == null ? null : -Math.abs(l.amountRead);
  return l.computed ?? l.amountRead;
}

/** 検算の材料の1行（画面の行・API の行のどちらも満たす形） */
export type ReceivedCheckLine = {
  kind?: ReceivedLineKind;
  name: string;
  spec?: string;
  sheetNo?: number;
  sheetName?: string;
  sheetKind?: "cover" | "breakdown";
  amountRead: number | null;
  computed: number | null;
};
export type ReceivedCheckAll = { read: number | null; sum: number | null; check: ReceivedCheck; taxIncluded: boolean; notes: string[] };

const yenText = (n: number) => n.toLocaleString("ja-JP");
const pctText = (r: number) => `${Math.round(r * 10_000) / 100}%`;

/**
 * 階層の検算（S5。純粋な関数・API の normalize と画面が同じこれを呼ぶ）。小計・合計の決まりは自社の見積と同じ（docs/17・db/test_rate_rows.sql ②）。
 *   ① 内訳の紙：小計の行＝前の小計の次からの 合計に入れる行の和、合計の行＝その紙の全部の和（小計も合計も無い紙は比べない）
 *   ② 表紙：上から順に足し（内訳の合計の行はその内訳の和で）、小計・合計の行に来るたびに「そこまでの和」と比べる。
 *      内訳の合計の行の金額はその内訳の和とも比べる。内訳の紙があるのに表紙に内訳の合計の行が無ければ Σ内訳の和を先頭に。
 *      何も足す前の小計・合計の行（頭の「御見積金額」）は比べない。消費税の行までの最後の値＝税抜（読み取った合計として出す）
 *   ③ 税：税抜 × 税率（切り捨て・四捨五入・切り上げのどれか）＝消費税の行、税抜＋消費税＝消費税のあとの合計の行。税率は消費税の行の「(10%)」、無ければ取引日の標準税率
 *   表紙に小計・合計・消費税の行が無い書類（1枚の注文書）は S4 のまま：行の合計＝読み取った合計の欄（totalText）、合わなければ税込も見る。
 *   合わない段は1行の文。前の段と同じ差のまま続く段は書かない（差が変わった所だけ）。最後の差額が1行の金額と同じなら手がかりを1つ。
 *   textRead＝読み取った合計の欄（totalText）の円、taxIncludedHint＝書類の税込／税抜（読み取り）、taxRate＝取引日の標準税率（0.1 の形。引けなければ null）
 */
export function receivedCheckAll(lines: ReceivedCheckLine[], total: { textRead: number | null; taxIncludedHint?: boolean | null }, taxRate: number | null): ReceivedCheckAll {
  const M = RECEIVED_READ_MSG;
  const notes: string[] = [];
  let compared = false;
  let lastGap = 0;
  const sum = receivedLineSum(lines);
  const stageName = (l: ReceivedCheckLine) => receivedLabel(l.name) || RECEIVED_LINE_KIND_LABEL[l.kind ?? "detail"];
  const lineName = (l: ReceivedCheckLine) => l.name.trim() || (l.spec ?? "").trim() || RECEIVED_LINE_KIND_LABEL[l.kind ?? "detail"];

  // 紙に分ける（sheetNo が同じ続きの行。無ければ表紙）
  type Run = { no: number; kind: "cover" | "breakdown"; name: string; lines: ReceivedCheckLine[] };
  const runs: Run[] = [];
  for (const l of lines) {
    const no = l.sheetNo ?? 0;
    const last = runs[runs.length - 1];
    if (last && last.no === no) last.lines.push(l);
    else runs.push({ no, kind: l.sheetKind ?? "cover", name: (l.sheetName ?? "").trim(), lines: [l] });
  }

  // ① 内訳の紙：小計の区切り（節）ごとと紙全体の和
  type Section = { run: Run; label: string; sum: number; printed: number | null };
  const sections: Section[] = [];
  for (const run of runs) {
    if (run.kind !== "breakdown") continue;
    const label = run.name || M.sheetBreakdown;
    let acc = 0;
    let all = 0;
    let gap = 0;
    for (const l of run.lines) {
      const v = receivedLineValue(l);
      if (v != null) {
        acc += v;
        all += v;
      } else if (l.kind === "subtotal" && l.amountRead != null) {
        compared = true;
        const secLabel = [l.spec ?? "", l.name].map(sheetKey).find((x) => x !== "") ?? "";
        sections.push({ run, label: secLabel || label, sum: acc, printed: l.amountRead });
        if (acc !== l.amountRead) {
          notes.push(M.checkSheet(secLabel || label, yenText(acc), stageName(l), yenText(l.amountRead)));
          gap += acc - l.amountRead;
          lastGap = acc - l.amountRead;
        }
        acc = 0;
      } else if (l.kind === "total" && l.amountRead != null) {
        compared = true;
        const d = all - l.amountRead;
        if (d !== gap) {
          notes.push(M.checkSheet(label, yenText(all), stageName(l), yenText(l.amountRead)));
          lastGap = d - gap;
          gap = d;
        }
      }
    }
    sections.push({ run, label, sum: all, printed: null });
  }

  // ② 表紙
  const matchOf = (l: ReceivedCheckLine): Section | null => {
    const a = l.amountRead;
    if (a != null) {
      const bySum = sections.find((s) => s.sum === a);
      if (bySum) return bySum;
      const byPrinted = sections.find((s) => s.printed === a);
      if (byPrinted) return byPrinted;
    }
    const k = sheetKey(l.name);
    return k ? (sections.find((s) => sheetKey(s.label) === k || sheetKey(s.run.name) === k) ?? null) : null;
  };
  const cover = runs.filter((r) => r.kind === "cover").flatMap((r) => r.lines);
  const referenced = new Set<Run>();
  for (const l of cover) {
    const m = l.kind === "sheetTotal" ? matchOf(l) : null;
    if (m) referenced.add(m.run);
  }
  let run = 0;
  for (const s of sections) if (s.printed === null && !referenced.has(s.run)) run += s.sum; // 表紙に内訳の合計の行が無い内訳は先頭に
  let started = run !== 0;
  let gap = 0;
  let coverStage = false;
  let taxLine: ReceivedCheckLine | null = null;
  let net: number | null = null;
  const afterTax: ReceivedCheckLine[] = [];
  for (const l of cover) {
    if (taxLine) {
      if ((l.kind === "total" || l.kind === "subtotal") && l.amountRead != null) afterTax.push(l);
      continue;
    }
    const v = receivedLineValue(l);
    if (v != null) {
      run += v;
      started = true;
    } else if (l.kind === "sheetTotal") {
      started = true;
      const m = matchOf(l);
      if (m) {
        run += m.sum;
        compared = true;
        if (l.amountRead != null && l.amountRead !== m.sum) {
          notes.push(M.checkCoverSheet(lineName(l), yenText(l.amountRead), m.label, yenText(m.sum)));
          gap += m.sum - l.amountRead;
          lastGap = m.sum - l.amountRead;
        }
      } else if (l.amountRead != null) {
        run += l.amountRead; // 当たる内訳が無い（人が区分を変えた）：書かれた金額で
      }
    } else if ((l.kind === "subtotal" || l.kind === "total") && l.amountRead != null) {
      if (!started) continue;
      compared = true;
      coverStage = true;
      const d = run - l.amountRead;
      if (d !== gap) {
        notes.push(M.checkUpTo(yenText(run), stageName(l), yenText(l.amountRead)));
        lastGap = d - gap;
        gap = d;
      }
    } else if (l.kind === "tax") {
      taxLine = l;
      net = run - gap;
    }
  }
  let read: number | null;
  let taxIncluded = false;
  if (coverStage || taxLine) {
    net ??= run - gap;
    read = net;
    compared = true;
    if (sum != null && sum !== net) {
      notes.push(M.checkSumNet(yenText(sum), yenText(net)));
      lastGap = sum - net;
    }
    const T = taxLine?.amountRead ?? null;
    if (taxLine && T != null) {
      const rate = rateValue(receivedRateText(taxLine.name, taxLine.spec)) ?? taxRate;
      if (rate != null && rate > 0 && !taxCandidates(net, rate).includes(T)) notes.push(M.checkTax(yenText(T), yenText(net), pctText(rate)));
      for (const l of afterTax) if (l.amountRead !== net + T) notes.push(M.checkTaxIn(stageName(l), yenText(l.amountRead as number), yenText(net), yenText(T)));
    }
    const tr = total.textRead;
    if (tr != null && tr !== net && !(T != null && tr === net + T) && receivedTotalCheck(tr, net, taxRate, total.taxIncludedHint ?? null).check !== "ok") {
      notes.push(M.checkTextNet(yenText(tr), yenText(net)));
    }
  } else {
    read = total.textRead;
    const tc = receivedTotalCheck(total.textRead, sum, taxRate, total.taxIncludedHint ?? null);
    taxIncluded = tc.taxIncluded;
    if (tc.check !== "none") compared = true;
    if (tc.check === "mismatch" && sum != null && total.textRead != null) {
      notes.push(M.checkSumRead(yenText(sum), yenText(total.textRead)));
      lastGap = sum - total.textRead;
    }
  }
  // 差額の手がかり：多すぎる（＋）なら合計に入れた行、足りない（−）なら入れていない行から、同じ金額の最初の1行
  if (notes.length && lastGap !== 0) {
    const d = Math.abs(lastGap);
    const hit =
      lastGap > 0
        ? lines.find((l) => Math.abs(receivedLineValue(l) ?? NaN) === d)
        : lines.find((l) => !receivedInTotal(l.kind) && !receivedCheckOnly(l.kind) && l.amountRead != null && Math.abs(l.amountRead) === d);
    if (hit) notes.push(M.checkGapHint(yenText(d), lineName(hit)));
  }
  return { read, sum, check: notes.length ? "mismatch" : compared ? "ok" : "none", taxIncluded, notes };
}

/**
 * 読み取った1行（見積の行の形＋検算、S4）。
 *   amountRead＝書類の金額（rd-1.0 の amount を円に。無い・読めなければ null）、computed＝数量×単価（会社の明細の丸め）、check＝その2つの検算、
 *   unsure＝単価が書類に無く 金額÷数量 で入れた（割り切れたときだけ。「1式」で数量が無ければ数量 1）＝画面は単価を淡い地
 */
export const ReceivedReadLine = LineInput.extend({
  amountRead: z.number().int().nullable(),
  computed: z.number().int().nullable(),
  check: ReceivedCheck,
  unsure: z.boolean(),
  /** 区分（S5。classifyReceivedLines が決め、人がセレクトで変えられる） */
  kind: ReceivedLineKind,
  /** 紙（S5）：何枚目か（0〜）・紙の名前（読み取ったまま。表紙は空のことがある）・表紙か内訳か */
  sheetNo: z.number().int().min(0),
  sheetName: z.string(),
  sheetKind: z.enum(["cover", "breakdown"]),
  /** 率の行の率の文字（「8%」。率のときだけ） */
  rateText: z.string().nullable(),
});
export type ReceivedReadLine = z.infer<typeof ReceivedReadLine>;

/**
 * 合計の検算（S4・S5）。taxRate＝取引日（書類の日付、無ければ今日）の標準税率（0.1 の形。引けなければ null）＝画面が計算し直すとき使う。
 * read＝読み取った合計（表紙に小計・合計・消費税の行があれば 消費税までの税抜、無ければ totalText）、textRead＝読み取った合計の欄（totalText）の円＝画面が計算し直す材料、
 * notes＝合わない段の文（S5。空なら一致）
 */
export const ReceivedReadTotal = z.object({
  read: z.number().int().nullable(),
  sum: z.number().int().nullable(),
  check: ReceivedCheck,
  taxIncluded: z.boolean(),
  taxRate: z.number().nullable(),
  textRead: z.number().int().nullable(),
  notes: z.array(z.string()),
});
export type ReceivedReadTotal = z.infer<typeof ReceivedReadTotal>;

/**
 * 直した読み取り（GET …/read の result。保存しない＝返すたびに直す）。
 *   issueDate＝書類の日付（和暦も西暦に。読めなければ null＝文字は datedText に残る）、periodText＝納期の文字そのまま（{period}）、
 *   total＝合計の検算（S4。read＝読み取った合計の円）、lines＝見積の行の形＋検算（単価が書類に無い行は null＝埋めない。金額÷数量が割り切れれば unsure で入れる）。
 *   checks＝「確かめて」を出す所（信頼度が 0.89 より下・数字が読めなかった行）
 */
export const ReceivedRead = z.object({
  version: z.string(),
  kindGuess: z.enum(["order", "inquiry", "other"]),
  sender: z.object({ company: z.string(), person: z.string(), tel: z.string(), fax: z.string() }),
  docNo: z.string(),
  issueDate: z.string().nullable(),
  datedText: z.string(),
  subject: z.string(),
  deliverTo: z.string(),
  periodText: z.string(),
  taxIncluded: z.boolean().nullable(),
  total: ReceivedReadTotal,
  lines: z.array(ReceivedReadLine),
  bodyText: z.string(),
  confidence: z.object({ head: z.number(), lines: z.number() }),
  checks: z.object({ head: z.boolean(), lines: z.array(z.boolean()) }),
});
export type ReceivedRead = z.infer<typeof ReceivedRead>;

/** 名称の候補（前の見積の行・品目マスタ）。選ぶと 名称・規格・単位・（品目なら）品目・（前の行なら）単価 が入る */
export const ReceivedCandidate = z.object({
  source: z.enum(["past", "item"]),
  name: z.string(),
  spec: z.string(),
  unit: z.string(),
  unitPrice: z.number().int().nullable(),
  itemId: z.string().uuid().nullable(),
  /** 一致の強さ：exact＝同じ／contains＝候補が読んだ名称を含む／within＝読んだ名称が候補を含む */
  match: z.enum(["exact", "contains", "within"]),
});
export type ReceivedCandidate = z.infer<typeof ReceivedCandidate>;

/** 下書きの見積に追加の先（その{project}の下書きの見積）。version は PUT と同じ版（違えば 409 ESTIMATE_STALE） */
export const ReceivedImportDraft = z.object({ id: z.string().uuid(), subject: z.string(), lineCount: z.number().int(), version: z.string() });
export type ReceivedImportDraft = z.infer<typeof ReceivedImportDraft>;

/** GET /received-documents/:id/read */
export const ReceivedReadView = z.object({
  id: z.string().uuid(),
  status: ReceivedReadStatus,
  requestedAt: z.string().nullable(),
  doneAt: z.string().nullable(),
  error: z.string().nullable(),
  result: ReceivedRead.nullable(),
  /** 行ごとの名称の候補（result.lines と同じ並び。5つまで） */
  candidates: z.array(z.array(ReceivedCandidate)),
  /** 差出人を取引先で引いた結果。mismatch＝案件の{customer}と違うようだ（案件は変えない） */
  sender: z.object({ match: z.object({ partnerId: z.string().uuid(), name: z.string() }).nullable(), mismatch: z.boolean() }),
  quota: z.object({ used: z.number().int(), limit: z.number().int().nullable() }),
  /** 下書きの見積に追加の先（その{project}の下書きの見積。無ければ空） */
  drafts: z.array(ReceivedImportDraft),
  /** 画面で「数量×単価」を見せるときの明細の丸め（会社の設定。正は見積にしたあとの DB の値） */
  lineRounding: RoundingCode,
  /**
   * 請求書を仕入にする（#233 3a、書類が「請求書」で sales の会社だけ。ほかは null）。
   * vendor＝差出人を{vendor}で引いた結果（無ければ null＝選ぶ）・vendors＝選べる{vendor}・orders＝候補の{order}（{vendor}の承諾済で、まだ仕入の無いもの）
   */
  purchase: z
    .object({
      vendor: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
      vendors: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
      orders: z.array(z.object({ id: z.string().uuid(), vendorId: z.string().uuid(), displayNo: z.string().nullable(), subject: z.string(), groupName: z.string(), total: z.number().int() })),
    })
    .nullable()
    .default(null),
});
export type ReceivedReadView = z.infer<typeof ReceivedReadView>;

/**
 * 取り込む1行（S5）：見積の行の形＋区分と紙。区分の無い行（S4 までの画面）は見積の行の形のまま入れる。
 * API が receivedImportLines で見積の区分に写し、並べ直す
 */
export const ReceivedImportLine = LineInput.extend({
  kind: ReceivedLineKind.optional(),
  sheetNo: z.number().int().min(0).max(999).optional(),
  sheetName: z.string().max(200).optional(),
  sheetKind: z.enum(["cover", "breakdown"]).optional(),
  rateText: z.string().max(20).nullable().optional(),
  amountRead: z.number().int().nullable().optional(),
});
export type ReceivedImportLine = z.infer<typeof ReceivedImportLine>;

/** 内訳の紙の名前で見出しを置かない言葉（「内訳書」は見積の見出しにしても意味が無い） */
const GENERIC_SHEET = /^(表紙|内訳|内訳書|内訳明細|内訳明細書|明細|明細書|見積書|御見積書|見積明細|見積明細書)$/;

/**
 * 読み取った行を見積の行にする（S5、R5 §3-2。純粋な関数・API の import だけが呼ぶ）。
 *   明細 → 明細／見出し → 見出し（group）／説明 → 説明／別途 → 別途（金額の欄の文字。数量・単価は空）／値引き → 値引き（マイナスの金額）／
 *   率 → 金額の決まった明細（数量 1・式・単価＝読み取った金額・備考の先頭に率の文字）＝基礎が読み取れないので率の行（overhead_rate）にはしない（直すのは人）。
 *   小計・合計・消費税・内訳の合計は入れない（見積が計算する）。
 *   並び：内訳の紙ごとに 見出し（紙の名前。空・「内訳書」なら置かない・最初の行が同じ名前の見出しなら重ねない）→ その紙の行、最後に表紙の行
 */
export function receivedImportLines(lines: ReceivedImportLine[], newId: () => string): LineInput[] {
  const toLine = (l: ReceivedImportLine): LineInput | null => {
    const { kind, sheetNo: _n, sheetName: _s, sheetKind: _k, rateText, amountRead, ...base } = l;
    void [_n, _s, _k];
    const empty = { quantity: null, unit: "", unitPrice: null, amount: null, rate: null, rateBase: null, rateBaseLineIds: [], discountTargetTotal: null, amountLabel: null };
    switch (kind) {
      case undefined:
        return LineInput.parse(base);
      case "detail":
        // 人が小計などから「明細」に変えた行も明細で（読み取りの行の role は表示の形）
        return LineInput.parse({ ...base, role: "detail", amount: null, amountLabel: null, discountTargetTotal: null, rate: null, rateBase: null, rateBaseLineIds: [] });
      case "heading":
        return LineInput.parse({ ...base, ...empty, role: "group" });
      case "note":
        return LineInput.parse({ ...base, ...empty, role: "note" });
      case "excluded":
        return LineInput.parse({ ...base, ...empty, role: "excluded", amountLabel: (base.amountLabel ?? "").trim().slice(0, 20) || RECEIVED_EXCLUDED_WORDS[0] });
      case "discount": {
        const a = amountRead ?? base.amount ?? null;
        return LineInput.parse({ ...base, ...empty, role: "discount", amount: a == null ? null : -Math.abs(a) });
      }
      case "rate": {
        const a = amountRead ?? base.unitPrice ?? null;
        // 備考の先頭に率の文字（備考にもう書いてあれば重ねない）
        const has = !!rateText && base.note.normalize("NFKC").includes(rateText);
        const note = (has ? base.note : [rateText ?? "", base.note].filter((x) => x.trim() !== "").join(" ／ ")).slice(0, 200);
        return LineInput.parse({ ...base, ...empty, role: "detail", quantity: 1, unit: "式", unitPrice: a, note });
      }
      default:
        return null; // 小計・合計・消費税・内訳の合計
    }
  };
  // 紙ごとに分ける（sheetNo が同じ続き）。紙の無い行（前の形）はそのまま表紙の扱い
  type Run = { no: number | null; kind: "cover" | "breakdown"; name: string; lines: ReceivedImportLine[] };
  const runs: Run[] = [];
  for (const l of lines) {
    const no = l.sheetNo ?? null;
    const last = runs[runs.length - 1];
    if (last && last.no === no) last.lines.push(l);
    else runs.push({ no, kind: l.sheetKind ?? "cover", name: (l.sheetName ?? "").trim(), lines: [l] });
  }
  const out: LineInput[] = [];
  for (const run of [...runs.filter((r) => r.kind === "breakdown"), ...runs.filter((r) => r.kind !== "breakdown")]) {
    const mapped = run.lines.map(toLine).filter((x): x is LineInput => x !== null);
    if (run.kind === "breakdown") {
      const name = run.name.slice(0, 200);
      const label = receivedLabel(name);
      const first = mapped[0];
      const dup = first?.role === "group" && receivedLabel(first.name) === label;
      if (label && !GENERIC_SHEET.test(label) && !dup && mapped.length) out.push(LineInput.parse({ id: newId(), role: "group", name }));
    }
    out.push(...mapped);
  }
  return out;
}

/** POST /received-documents/:id/import */
export const ReceivedImport = z
  .object({
    /** new＝見積を作成／append＝下書きの見積に追加／purchase＝仕入を登録（#233 3a。書類が「請求書」で sales の会社） */
    target: z.enum(["new", "append", "purchase"]),
    documentId: z.string().uuid().optional(),
    version: z.string().optional(),
    /** 仕入の{vendor}・{order}（target=purchase） */
    vendorId: z.string().uuid().optional(),
    orderId: z.string().uuid().nullable().optional(),
    /** 仕入の消費税（読み取った消費税の行の和。無ければ省く＝明細から計算） */
    taxAmount: z.number().int().min(-9_999_999_999).max(9_999_999_999).nullable().optional(),
    head: z.object({
      subject: z.string().trim().max(200).default(""),
      periodText: z.string().trim().max(100).default(""),
      siteAddress: z.string().trim().max(200).optional(),
      deliverTo: z.string().trim().max(400).optional(),
      issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
      /** 番号（請求書番号。target=purchase のとき仕入の相手の請求書番号に） */
      docNo: z.string().trim().max(100).optional(),
    }),
    lines: z.array(ReceivedImportLine).min(1, "行を1つ以上入れてください").max(500),
    /** 人が直した行の数（確認の引き出しで変えた・足した行。intake_events.corrected_lines） */
    correctedLines: z.number().int().min(0).max(500).default(0),
  })
  .refine((v) => v.target !== "append" || !!v.documentId, { message: "追加する見積を選んでください", path: ["documentId"] })
  .refine((v) => v.target !== "purchase" || !!v.vendorId, { message: "{vendor}を選んでください", path: ["vendorId"] });
export type ReceivedImport = z.infer<typeof ReceivedImport>;

export const ReceivedImportResult = z.object({
  /** 作った・足した見積の id（target=purchase のときは仕入の id） */
  documentId: z.string().uuid(),
  documentNo: z.string().nullable(),
  lineCount: z.number().int(),
  message: z.string(),
  target: z.enum(["estimate", "purchase"]).default("estimate"),
});
export type ReceivedImportResult = z.infer<typeof ReceivedImportResult>;

/**
 * 読み取りの画面・API の文（{customer}・{project}・{vendorQuote} は会社の言葉＝画面は t()、API は fillLabels）。
 * S3（R3、2026-10-11 夜）：画面で「AI」と名乗る（「AI読み取り」。「AI-OCR」「OCR」は書かない）。ボタンは名詞形（docs/10 §5.1.2）
 */
export const RECEIVED_READ_MSG = {
  /** 有料オプションの名前（料金プラン・紹介ページ・特定商取引法の表記・管理アプリ） */
  optionName: "AI書類読み取り",
  optionHelp: "AI が受け取った注文書・見積依頼を読み取り、見積の下書きにできます。{vendorQuote}のAI読み取りと合わせて月 {n}通まで。",
  /** 料金プランの各プランの1行 */
  readsLine: (n: number) => `AI読み取り 月 ${n}通（{vendorQuote}・受け取った書類）`,
  /** 枠の「？」（オプションのある会社） */
  help: "{customer}から受け取った注文書・見積依頼・図面・写真などを{project}に置いておく枠です。ファイルはこの枠か画面のどこかにドロップするか、「ファイル選択」で入れます。文字は {paste} で貼り付けると書類になります。注文書と見積依頼は「AI読み取り」で中身を読み、原本と見比べてから見積の下書きにできます。{vendor}からの請求書は仕入の下書きにできます。原本はそのまま保存し、削除しても「削除済」から復元できます。",
  read: "AI読み取り",
  reading: "AI読み取り中",
  /** 処理役がまだ取りに来ていない（read_status=queued）。{vendorQuote}の待ちと同じ点線の丸 */
  queuedLabel: "順番待ち",
  /** 読み取り中の目安（薄墨）。1ページの FAX で 9〜13秒、待ちを入れて */
  etaHint: "約30秒",
  /** 引き出しを読み取り中に開いたとき（回る丸の右） */
  readingDrawer: "AI読み取り中…",
  done: "読み取り済み ›",
  failed: "読み取れませんでした",
  again: "もう一度",
  /** 月の上限で押せないボタンの横（paren なし＝ボタンの横の1語） */
  quotaFull: (limit: number) => `今月の上限 ${limit}通`,
  /** 未選択の行の状態の列（doc_read の会社だけ。図面・写真・資料・その他は空）。S4：名詞＋「を選択」の形（docs/10 §5.1.2） */
  pickKindHint: "書類選択",
  /** 一覧の行の警告（S4 ①。朱の「⚠ 読み取り済み ›」の下・title） */
  warnSender: "差出人が{customer}と異なります",
  // スマホ（幅 767px 以下・スマホ版）は AI読み取りのボタンと引き出しを出さない＝状態は文字だけ（S4 ⑥）
  phoneDone: "読み取り済み（確認は PC で）",
  phoneDoneWarn: "読み取り済み・差出人が{customer}と異なります（確認は PC で）",
  redo: "再読み取り",
  redoBody: "読み取った内容と修正した内容は消え、もう一度読み取ります。",
  imported: "取込済",
  importedDraft: "下書き",
  quota: (used: number, limit: number) => `今月のAI読み取り ${used}／${limit}通`,
  drawerTitle: "AI読み取り結果",
  /** 引き出しの下の段（主ボタンの上。薄墨 14px・ⓘ。2026-10-11 射場さんの決定の文） */
  notice: "AI が読み取った内容です。原本と見比べてから見積にしてください。",
  original: "原本",
  /** 幅 767px 以下の原本のたたんだ行の「›」の読み上げ */
  originalShow: "原本表示",
  originalHide: "原本をたたむ",
  // 原本のツールバー（S3。S4 で「幅に合わせる」「原本を広く／内容を広く」はアイコン＝この文は aria-label と title）
  zoomOut: "縮小",
  zoomIn: "拡大",
  fitWidth: "幅に合わせる",
  zoomLabel: "倍率",
  /** 境の切替（ドラッグだけにしない＝WCAG 2.2 SC 2.5.7） */
  widenOriginal: "原本を広く",
  widenContent: "内容を広く",
  splitter: "原本と内容の境",
  kind: "書類",
  kindSuggest: { order: "注文書のようです", inquiry: "見積依頼のようです" } as Record<"order" | "inquiry", string>,
  kindSet: { order: "注文書にする", inquiry: "見積依頼にする" } as Record<"order" | "inquiry", string>,
  sender: "差出人",
  senderMismatch: "差出人（{sender}）は{customer}と異なります",
  date: "日付",
  docNo: "番号",
  body: "本文",
  unsure: "確かめて",
  // 検算（S4 ③。API の normalize.ts が出し、人が数量・単価を直すと画面が同じ関数で計算し直す）
  checkOk: "検算 一致",
  /** 読み取った合計が 行の合計＋消費税 と合った（税込の合計と比べた） */
  checkOkTax: "検算 一致（税込）",
  checkNg: "検算 不一致",
  /** 合わない行の金額の下（12px・朱） */
  readAmount: (n: string) => `読み取り ${n}`,
  /** 明細の段の右上。read が無ければ sumOnly */
  totalLine: (read: string, sum: string) => `読み取った合計 ${read}円　行の合計 ${sum}円`,
  sumOnly: (sum: string) => `行の合計 ${sum}円`,
  /** 行の合計が出せない（どの行にも金額が無い）ときは読み取った合計だけ */
  readOnly: (read: string) => `読み取った合計 ${read}円`,
  /** 単価を 金額÷数量 で入れたセルの title（淡い地） */
  priceFromAmount: "読み取った金額÷数量で入れた単価です。確かめてください",
  col: { no: "No", kind: "区分", name: "{lineName}", spec: "{lineSpec}", qty: "数量", unit: "単位", price: "単価", amount: "金額", note: "{lineNote}" },
  // 行の区分と階層の検算（S5、docs/13 §4.7。文は receivedCheckAll が使う。{customer}などは入らない＝t() は要らない）
  /** 小計・合計・消費税・内訳の合計の行（薄墨の文字）の title。押すとセレクトになる */
  notTaken: "検算のみ（取り込まない）",
  kindOf: (no: number) => `${no}行目の区分`,
  /** 紙の名前の帯（紙が2つ以上のとき）。名前が無ければ */
  sheetCover: "表紙",
  sheetBreakdown: "内訳",
  /** 区分の「？」（見積の ROW_ROLE_HELP の 小計・率・別途・値引き に足す2行） */
  kindHelpSheetTotal: "内訳の合計：表紙にある内訳の紙の合計の行。見積には入れません。",
  kindHelpCheckOnly: "小計・合計・消費税：検算にだけ使い、見積には入れません（見積が計算します）。",
  kindHelpRate: "率：読み取った金額のまま、数量 1・式 の明細として入れます（{lineNote}に率）。",
  checkSheet: (sheet: string, n: string, stage: string, m: string) => `内訳「${sheet}」の明細の和 ${n}円 ≠ ${stage} ${m}円`,
  checkCoverSheet: (name: string, n: string, sheet: string, m: string) => `表紙の「${name}」${n}円 ≠ 内訳「${sheet}」の和 ${m}円`,
  checkUpTo: (n: string, stage: string, m: string) => `そこまでの和 ${n}円 ≠ ${stage} ${m}円`,
  checkSumNet: (n: string, m: string) => `行の合計 ${n}円 ≠ 税抜 ${m}円`,
  checkSumRead: (n: string, m: string) => `行の合計 ${n}円 ≠ 読み取った合計 ${m}円`,
  checkTextNet: (m: string, n: string) => `読み取った合計 ${m}円 ≠ 税抜 ${n}円`,
  checkTax: (t: string, n: string, rate: string) => `消費税 ${t}円 ≠ 税抜 ${n}円 × ${rate}`,
  checkTaxIn: (stage: string, m: string, n: string, t: string) => `${stage} ${m}円 ≠ 税抜 ${n}円 ＋ 消費税 ${t}円`,
  checkGapHint: (d: string, name: string) => `差額 ${d}円は「${name}」の金額と同じです`,
  /** 検算の文は3行まで。4行目からは */
  checkMore: (n: number) => `ほか ${n}件`,
  pick: "候補",
  addLine: "行追加",
  deleteLine: "削除",
  lineMenu: "行の操作",
  createNew: "見積新規作成",
  appendTo: "下書き見積に追加",
  create: "見積作成",
  append: "下書き見積に追加",
  noDrafts: "下書きの見積がありません",
  noLines: "行を1つ以上入れてください",
  createdToast: (kindLabel: string) => `${kindLabel}から見積を作成しました`,
  appendedToast: (n: number) => `下書きの見積に ${n}行を追加しました`,
  stale: CONFLICT_MSG.ask,
  reload: "読み直す",
  // API の断り
  kindOnly: "注文書・見積依頼・請求書だけ読み取れます。書類を選んでください",
  busy: "読み取り中です。終わるまでお待ちください",
  monthLimit: (used: number, limit: number) => `今月のAI読み取りは上限（${limit}通）に達しました（今月 ${used}通）`,
  notDone: "読み取りが終わっていません",
  importedAlready: "この書類はもう見積・仕入に取り込んでいます（その見積・仕入の下書きを削除すると、もう一度読み取り・取込ができます）",
  target: "この{project}の下書きの見積だけに追加できます",
  deleted: "削除した書類は読み取れません",
} as const;
